import { createContext, useCallback, useContext, useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { AgainLine, EmptyState, LastUpdate, Recovery, RetryAction, TechnicalDetails, type Copy, type RecoveryRead } from '@beyond-js/ui/react';
import type { Language } from '@snapshots/locale';
import { Missing } from './missing';
import { Outage, type Missed } from './outage';
import { Problems } from './problems';

type Text = (key: string, values?: Record<string, string | number>) => string;

/** What the shared notice takes from this product: its words in the person's language, and each read with its evidence */
class Notice {
	static #labels = new Map<Language, Copy>();

	/** The words of Try again, the last-tried line and Technical details, made once per language so each notice is made once */
	static labels(language: Language): Copy {
		if (!Notice.#labels.has(language)) Notice.#labels.set(language, { ...RetryAction.labels[language], ...AgainLine.labels[language], ...TechnicalDetails.labels[language] });
		return Notice.#labels.get(language)!;
	}

	/** A read as the notice takes it: its evidence is what Technical details holds (D43), a row per read */
	static read(missed: Missed): RecoveryRead {
		return { key: missed.key, title: missed.title, details: Problems.details(missed.refusal), run: missed.run };
	}
}

/**
 * A page's outage, said once (decision D163, `@beyond-js/ui` 0.24.0): one line per who did not answer, in
 * the page's notice region on the H1's edge, in the warning tone. It says who did not answer, when it last
 * tried and one Try again, and lists nothing; it tries again by itself on the family's schedule (D132), and
 * every read it holds is a row of its one Technical details. With `whole`, nothing on the page could be
 * read, and the line is the page's body in `Unavailable`'s form. A read leaves it when it leaves `reads`:
 * only the page knows it answered.
 */
export function Outages({ reads, whole = false, text, language }: { reads: Missed[]; whole?: boolean; text: Text; language: Language }) {
	const host = useRef<HTMLDivElement>(null);
	// When the last read answers while focus is in the line, focus goes to the page's heading
	const focus = useCallback(() => host.current?.closest('.bui-page')?.querySelector<HTMLElement>('.bui-page-heading') ?? null, []);
	const groups = Outage.group(reads);
	return (
		// Every cause keeps its notice on the page, hidden while it holds nothing, so a notice moves focus out of
		// itself before it hides; the stylesheet hides this region while none shows, so it takes no room
		<div ref={host} data-outages={whole ? 'page' : 'line'} className="flex flex-col gap-3">
			{Outage.causes.map(cause => {
				const group = groups.get(cause) ?? [];
				return <Recovery key={cause} title={Outage.title(text, cause)} reads={group.map(read => Notice.read(read))} whole={whole && group.length > 0} labels={Notice.labels(language)} locale={language} focus={focus} />;
			})}
		</div>
	);
}

/**
 * A region whose read nobody answered, in its own place (D163): "Not available right now" with the outage's
 * mark, and no cause and no Try again of its own, which are the page's line. A failed read is never drawn as
 * empty. `frame` is false inside a region that is framed already.
 */
export function Unanswered({ language, frame }: { language: Language; frame?: boolean }) {
	return (
		<div data-unanswered>
			<EmptyState kind="outage" frame={frame} labels={EmptyState.labels[language]} />
		</div>
	);
}

/**
 * Above content kept from an earlier read while it is not read again (D163): "Last updated 12:04", the time
 * of the last read that answered, or "May be out of date" when that time is not known, which is never invented.
 */
export function Updated({ time, language }: { time?: Date; language: Language }) {
	return (
		<div data-updated>
			<LastUpdate time={time ?? null} locale={language} labels={LastUpdate.labels[language]} />
		</div>
	);
}

const Scope = createContext<Missing | null>(null);

/** Where a page's regions report the reads nobody answered (`useOutage`), for its one line */
export function OutageScope({ missing, children }: { missing: Missing; children: ReactNode }) {
	return <Scope.Provider value={missing}>{children}</Scope.Provider>;
}

/** The reads a page's regions report unanswered, as the page draws them */
export function useMissing(missing: Missing): Missed[] {
	return useSyncExternalStore(missing.listen, () => missing.reads);
}

/** Reports a region's read nobody answered (`missed`), or that it answered (`null`), while the region is on the page */
export function useOutage(key: string, missed: Omit<Missed, 'key'> | null): void {
	const missing = useContext(Scope);
	useEffect(() => {
		missing?.set(key, missed ? { ...missed, key } : null);
	});
	useEffect(() => () => missing?.set(key, null), [missing, key]);
}
